import React, { useEffect } from 'react';
import { formatNumber, formatDifference, formatDateRanges, formatRank } from '../../utils/formatting';
import { getValueColor } from '../../utils/coloring';
import { sortData, requestSort, getSortIcon } from '../../utils/sorting';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import { useTableOverflow, useMobileState } from '../../hooks/tables';
import './Tables.css';
import './LocationType.css';

const LocationTypeTrendsTable = ({ 
  data,
  locationType,
  onLocationSelect,
  selectedLocation,
  selectedCrime, 
  selectedDateRange,
  sortConfig,
  onSortChange,
  currentPage,
  itemsPerPage,
  compareMode,
  setHoveredLocation
}) => {
  const isMobile = useMobileState();
  useTableOverflow();

  useEffect(() => {
    const scrollTablesToRight = () => {
        const trendTables = document.querySelectorAll('.location-type-table.trends');
        
        const scrollTableRight = (table) => {
          const container = table.closest('.location-type-table-container') || table.parentElement;
          if (container) {
            container.scrollLeft = container.scrollWidth;
          }
        };
        
        trendTables.forEach(scrollTableRight);
    };
  
    const timer = setTimeout(scrollTablesToRight, 0);
    
    return () => clearTimeout(timer);
  }, []);

  const isMetro = locationType === 'metro';
  const isCounty = locationType === 'county';
  const isCity = locationType === 'city';
  const isCountyOrCity = isCounty || isCity;
  const isAllCountiesNational = isCounty && selectedLocation?.info?.name === 'All Counties';
  const isAllCitiesNational = isCity && selectedLocation?.info?.name === 'All Cities';
  const isAllNational = isAllCountiesNational || isAllCitiesNational;
  const locationPrefix = locationType.charAt(0).toUpperCase() + locationType.slice(1);
  const crimePrefix = selectedCrime.toLowerCase();

  let stateAbbr = '';
  if (isCountyOrCity && data?.locations?.length > 0) {
    stateAbbr = getStateAbbreviation(data.locations[0]?.state || '');
  }
  
  const sortedData = sortData(data.locations, sortConfig);
  const currentRows = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleSort = (key) => requestSort(key, sortConfig, onSortChange);

  const rankField = isCountyOrCity
    ? compareMode === 'state'
      ? `${crimePrefix}_state_trend_rank`
      : `${crimePrefix}_national_trend_rank`
    : `${crimePrefix}_trend_rank`;

  const formatLocationName = (location) => {
    if (isCounty) {
      return (location.county === 'Carson City' && location.state === 'Nevada') ?
        `${location.county}, ${getStateAbbreviation(location.state)}` :
        location.state === 'Louisiana' ?
          `${location.county} Parish, ${getStateAbbreviation(location.state)}` :
        location.state === 'Alaska' ?
          `${location.county} Borough, ${getStateAbbreviation(location.state)}` :
          `${location.county} County, ${getStateAbbreviation(location.state)}`;
    } else if (isCity) {
      return `${location.city}, ${getStateAbbreviation(location.state)}`;
    } else {
      return location.name;
    }
  };

  const formatLocationNameForSelection = (location) => {
    if (isCounty) {
      return (location.county === 'Carson City' && location.state === 'Nevada') ?
        `${location.county}, ${location.state}` :
        location.state === 'Louisiana' ?
          `${location.county} Parish, ${location.state}` :
        location.state === 'Alaska' ?
          `${location.county} Borough, ${location.state}` :
          `${location.county} County, ${location.state}`;
    } else if (isCity) {
      return `${location.city}, ${location.state}`;
    } else {
      return location.name;
    }
  };

  const getLocationSelectData = (location) => {
    const locationName = formatLocationNameForSelection(location);
    
    return {
      name: locationName,
      type: locationType,
      info: {
        name: locationName,
        type: locationType,
        ...((isCounty || isCity) && { state: location.state })
      }
    };
  };

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
        <div className="table-top-left">
          <div className="comparison-container">
            {isCountyOrCity && !isAllNational && <div style={{ height: '16px' }}></div>}
            <span>
              <strong>{formatDateRanges(selectedDateRange).currentRange}</strong>&nbsp;&nbsp;compared to&nbsp;&nbsp; 
              <strong>{formatDateRanges(selectedDateRange).prevRange}</strong>
            </span>
          </div>
        </div>
      </div>
      <div className="main-table-scroll-container">
        <table className={`main-table location-type-table ${(isCountyOrCity || isMetro) ? locationType : ''} trends`}>
          <thead>
            <tr>
              <th onClick={() => handleSort(rankField)} className="sortable">
                {isCountyOrCity && isMobile ? (
                  <>
                    <div>Rank</div>
                    {getSortIcon(rankField, sortConfig)}
                  </>
                ) : (
                  <div className="stacked-header">
                    <div>
                    {isCountyOrCity 
                      ? (compareMode === 'state' ? stateAbbr : 'US')
                      : ''
                    } {locationPrefix}
                    </div>
                    <div>Rank</div>
                    {getSortIcon(rankField, sortConfig)}
                  </div>
                )}
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                {locationPrefix}
                {getSortIcon('name', sortConfig)}
              </th>
              <th onClick={() => handleSort(`${crimePrefix}_count`)} className="sortable">
                <div className="stacked-header">
                  <div>Total</div>
                  <div>{getCrimeDisplayName(selectedCrime)}</div>
                  {getSortIcon(`${crimePrefix}_count`, sortConfig)}
                </div>
              </th>
              <th onClick={() => handleSort(`${crimePrefix}_count_prev`)} className="sortable">
                <div className="stacked-header">
                  <div>Previous</div>
                  <div>Total</div>
                  {getSortIcon(`${crimePrefix}_count_prev`, sortConfig)}
                </div>
              </th>
              <th onClick={() => handleSort(`${crimePrefix}_count_prev_diff`)} className="sortable">
                <div className="stacked-header">
                  <div>Change from</div>
                  <div>Previous Total</div>
                  {getSortIcon(`${crimePrefix}_count_prev_diff`, sortConfig)}
                </div>
              </th>
              <th onClick={() => handleSort(`${crimePrefix}_count_prev_diff_pct`)} className="sortable">
                <div className="stacked-header">
                  <div>% Change from</div>
                  <div>Previous Total</div>
                  {getSortIcon(`${crimePrefix}_count_prev_diff_pct`, sortConfig)}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map(location => (
              <tr key={isCountyOrCity ? `${location[isCounty ? 'county' : 'city']}-${location.state}` : location.name}>
                <td className="rank-cell">
                  {formatRank(location[rankField]) || '-'}
                </td>
                <td>
                  <span 
                    onMouseEnter={() => setHoveredLocation(formatLocationNameForSelection(location))}
                    onMouseLeave={() => setHoveredLocation(null)}
                    onClick={() => onLocationSelect(getLocationSelectData(location))}
                  >
                    {formatLocationName(location)}
                  </span>
                </td>
                <td className="number-cell">{formatNumber(location[`${crimePrefix}_count`])}</td>
                <td className="number-cell">{formatNumber(location[`${crimePrefix}_count_prev`])}</td>
                <td className="number-cell" style={{ color: getValueColor(location[`${crimePrefix}_count_prev_diff`]) }}>
                  {formatDifference(location[`${crimePrefix}_count_prev_diff`])}
                </td>
                <td className="number-cell bold" style={{ color: getValueColor(location[`${crimePrefix}_count_prev_diff_pct`]) }}>
                  {formatDifference(location[`${crimePrefix}_count_prev_diff_pct`], true)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LocationTypeTrendsTable;